import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, field, IntegerDecoder, MapDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { PropertyFilter } from '../filters/PropertyFilter.js';
import { GroupType } from '../GroupType.js';
import { RecordCategory } from './records/RecordCategory.js';
export var AskRequirement;
(function (AskRequirement) {
    AskRequirement["NotAsked"] = "NotAsked";
    AskRequirement["Optional"] = "Optional";
    AskRequirement["Required"] = "Required";
})(AskRequirement || (AskRequirement = {}));
export class FreeContributionSettings extends AutoEncoder {
    description = '';
    amounts = [5_0000, 15_0000, 30_0000];
}
__decorate([
    field({ decoder: StringDecoder })
], FreeContributionSettings.prototype, "description", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(IntegerDecoder) }),
    field({
        decoder: new ArrayDecoder(IntegerDecoder),
        upgrade: (oldValue) => {
            if (!Array.isArray(oldValue)) {
                return oldValue;
            }
            return oldValue.map(o => o * 100);
        },
        downgrade: (newValue) => {
            if (!Array.isArray(newValue)) {
                return newValue;
            }
            return newValue.map(o => Math.round(o / 100));
        },
        version: 389,
    })
], FreeContributionSettings.prototype, "amounts", void 0);
export class FinancialSupportSettings extends AutoEncoder {
    /**
     * E.g. 'financial support'
     */
    title = FinancialSupportSettings.defaultTitle;
    /**
     * E.g. 'We provide financial support for families in financial difficulties. You can ask for this by checking this checkbox'
     */
    description = FinancialSupportSettings.defaultDescription;
    /**
     * E.g. 'My family is in need of financial support'
     */
    checkboxLabel = FinancialSupportSettings.defaultCheckboxLabel;
    /**
     * E.g. 'SOMkort'
    */
    priceName = FinancialSupportSettings.defaultPriceName;
    /**
     * E.g. 'Uses financial support'
     */
    warningText = FinancialSupportSettings.defaultWarningText;
    /**
     * Whether a member can self assign financial support.
     * If false a member who chooses financial support cannot self subscribe.
     */
    preventSelfAssignment = false;
    /**
     * The text that a member sees if he cannot inscribe with financial support.
     */
    preventSelfAssignmentText = null;
    // todo translations
    static get defaultDescription() {
        return `We doen ons best om de kostprijs van onze activiteiten zo laag mogelijk te houden. Daarnaast voorzien we middelen om gezinnen die dat nodig hebben te ondersteunen. Om de drempel zo laag mogelijk te houden, voorzien we een discrete checkbox waarmee je kan aangeven dat je ondersteuning nodig hebt. We gaan hier uiterst discreet mee om.`;
    }
    // todo translations
    static get defaultTitle() {
        return `Financiële ondersteuning`;
    }
    // todo translations
    static get defaultPriceName() {
        return `Verlaagd tarief`;
    }
    // todo translations
    static get defaultCheckboxLabel() {
        return `Mijn gezin heeft nood aan financiële ondersteuning en ik wil dit discreet kenbaar maken`;
    }
    // todo translations
    static get defaultWarningText() {
        return `Gebruikt financiële ondersteuning`;
    }
    // todo translations
    static get defaultPreventSelfAssignmentText() {
        return `Er is goedkeuring nodig om in te schrijven met financiële ondersteuning. Gelieve de groep te contacteren.`;
    }
}
__decorate([
    field({ decoder: StringDecoder })
], FinancialSupportSettings.prototype, "title", void 0);
__decorate([
    field({ decoder: StringDecoder })
], FinancialSupportSettings.prototype, "description", void 0);
__decorate([
    field({ decoder: StringDecoder })
], FinancialSupportSettings.prototype, "checkboxLabel", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 289 })
], FinancialSupportSettings.prototype, "priceName", void 0);
__decorate([
    field({ decoder: StringDecoder, optional: true })
], FinancialSupportSettings.prototype, "warningText", void 0);
__decorate([
    field({ decoder: BooleanDecoder, optional: true, version: 319 })
], FinancialSupportSettings.prototype, "preventSelfAssignment", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 319 })
], FinancialSupportSettings.prototype, "preventSelfAssignmentText", void 0);
export class DataPermissionsSettings extends AutoEncoder {
    /**
     * E.g. 'financial support'
     */
    title = DataPermissionsSettings.defaultTitle;
    /**
     * E.g. 'We provide financial support for families in financial difficulties. You can ask for this by checking this checkbox'
     */
    description = DataPermissionsSettings.defaultDescription;
    /**
     * E.g. 'My family is in need of financial support'
     */
    checkboxLabel = DataPermissionsSettings.defaultCheckboxLabel;
    /**
     * Warning that is shown if the checkbox is not checked
     * E.g. 'Without this information the group will not be able to offer a reduced price if you are eligible for financial support.'
     */
    checkboxWarning = null;
    /**
     * Warning for administrators
     * E.g. 'Uses financial support'
     */
    warningText = DataPermissionsSettings.defaultWarningText;
    static get defaultDescription() {
        return '';
    }
    static get defaultTitle() {
        return $t(`%qa`);
    }
    static get defaultCheckboxLabel() {
        return $t(`%qb`);
    }
    static get defaultWarningText() {
        return $t(`%qc`);
    }
    static get defaultCheckboxWarning() {
        return '';
    }
}
__decorate([
    field({ decoder: StringDecoder })
], DataPermissionsSettings.prototype, "title", void 0);
__decorate([
    field({ decoder: StringDecoder })
], DataPermissionsSettings.prototype, "description", void 0);
__decorate([
    field({ decoder: StringDecoder })
], DataPermissionsSettings.prototype, "checkboxLabel", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 333 })
], DataPermissionsSettings.prototype, "checkboxWarning", void 0);
__decorate([
    field({ decoder: StringDecoder, optional: true })
], DataPermissionsSettings.prototype, "warningText", void 0);
export class OrganizationRecordsConfiguration extends AutoEncoder {
    // New record configurations
    /**
     * If the organizations provides support for families in financial difficulties
     */
    financialSupport = false;
    /**
     * Ask permissions to collect data
     */
    dataPermission = false;
    emailAddress = null;
    phone = null;
    gender = null;
    birthDay = null;
    address = null;
    parents = null;
    emergencyContacts = null;
    uitpasNumber = null;
    nationalRegisterNumber = null;
    /**
     * Collect data for tax certificates for childcare expenses (Belgium only)
     */
    taxCertificates = false;
    recordCategories = [];
    /**
     * Defines if optional record categories in the parent are enabled, and when they are enabled (using a filter)
     */
    inheritedRecordCategories = new Map();
    // General configurations
    freeContribution = null;
    static build(options) {
        // If not a default-age-group: disable enabled record categories in platform
        const platformConfig = options.platform.config.recordsConfiguration.clone();
        if ((!options.group || !options.group.defaultAgeGroupId) && !options.forceDefaultAgeGroup) {
            for (const c of platformConfig.recordCategories) {
                c.defaultEnabled = false;
            }
        }
        let organizationConfig = null;
        let defaultGroupConfig = null;
        let groupConfig = null;
        if (options.organization) {
            organizationConfig = options.organization.meta.recordsConfiguration.clone();
            if (options.group && options.group.type !== GroupType.Membership) {
                // Specifically delete all the record categories, as those are only enabled for normal memberships
                for (const c of organizationConfig.recordCategories) {
                    c.defaultEnabled = false;
                }
            }
            if (options.group && options.group.type === GroupType.WaitingList) {
                // All fields are disabled by default, but can be enabled in the settings of the group
                // We still keep all the record categories (defaultEnabled already has been set to false)
                // so they can be enabled again in the group
                organizationConfig = OrganizationRecordsConfiguration.create({
                    recordCategories: organizationConfig.recordCategories,
                });
            }
        }
        // Group config
        if (options.group && options.includeGroup) {
            groupConfig = options.group.settings.recordsConfiguration.clone();
        }
        if (options.group && options.group?.defaultAgeGroupId) {
            const defaultAgeGroupId = options.group.defaultAgeGroupId;
            defaultGroupConfig = options.platform.config.defaultAgeGroups.find(g => g.id === defaultAgeGroupId)?.recordsConfiguration.clone() ?? null;
        }
        return this.mergeChildren(...([platformConfig, defaultGroupConfig, organizationConfig, groupConfig].filter(f => f !== null)));
    }
    static mergeChildren(...configs) {
        if (configs.length === 0) {
            throw new Error('At least one configuration is required');
        }
        if (configs.length === 1) {
            return configs[0];
        }
        const first = configs[0];
        const second = configs[1];
        return this.mergeChildren(this.mergeChild(first, second), ...configs.slice(2));
    }
    /**
     * Note: best to not use this visually
     */
    static mergeChild(parent, child) {
        const clone = child.clone();
        if (parent.financialSupport !== false) {
            clone.financialSupport = parent.financialSupport;
        }
        if (parent.dataPermission !== false) {
            clone.dataPermission = parent.dataPermission;
        }
        if (parent.uitpasNumber !== null) {
            if (clone.uitpasNumber) {
                clone.uitpasNumber = clone.uitpasNumber.merge(parent.uitpasNumber);
            }
            else {
                clone.uitpasNumber = parent.uitpasNumber;
            }
        }
        if (parent.nationalRegisterNumber !== null) {
            if (clone.nationalRegisterNumber) {
                clone.nationalRegisterNumber = clone.nationalRegisterNumber.merge(parent.nationalRegisterNumber);
            }
            else {
                clone.nationalRegisterNumber = parent.nationalRegisterNumber;
            }
        }
        if (parent.taxCertificates !== false) {
            clone.taxCertificates = parent.taxCertificates;
        }
        if (parent.emailAddress !== null) {
            if (clone.emailAddress) {
                clone.emailAddress = clone.emailAddress.merge(parent.emailAddress);
            }
            else {
                clone.emailAddress = parent.emailAddress;
            }
        }
        if (parent.phone !== null) {
            if (clone.phone) {
                clone.phone = clone.phone.merge(parent.phone);
            }
            else {
                clone.phone = parent.phone;
            }
        }
        if (parent.gender !== null) {
            if (clone.gender) {
                clone.gender = clone.gender.merge(parent.gender);
            }
            else {
                clone.gender = parent.gender;
            }
        }
        if (parent.birthDay !== null) {
            if (clone.birthDay) {
                clone.birthDay = clone.birthDay.merge(parent.birthDay);
            }
            else {
                clone.birthDay = parent.birthDay;
            }
        }
        if (parent.address !== null) {
            if (clone.address) {
                clone.address = clone.address.merge(parent.address);
            }
            else {
                clone.address = parent.address;
            }
        }
        if (parent.parents !== null) {
            if (clone.parents) {
                clone.parents = clone.parents.merge(parent.parents);
            }
            else {
                clone.parents = parent.parents;
            }
        }
        if (parent.emergencyContacts !== null) {
            if (clone.emergencyContacts) {
                clone.emergencyContacts = clone.emergencyContacts.merge(parent.emergencyContacts);
            }
            else {
                clone.emergencyContacts = parent.emergencyContacts;
            }
        }
        if (parent.freeContribution !== null) {
            clone.freeContribution = parent.freeContribution;
        }
        clone.recordCategories = [...parent.recordCategories.map((r) => {
                const c = r.clone();
                if (r.defaultEnabled) {
                    return c;
                }
                const inheritedFilter = child.inheritedRecordCategories.get(r.id);
                if (inheritedFilter !== undefined) {
                    c.filter = inheritedFilter;
                    c.defaultEnabled = true;
                }
                return c;
            }), ...clone.recordCategories];
        // Nothing inherited yet
        clone.inheritedRecordCategories = new Map();
        return clone;
    }
}
__decorate([
    field({ decoder: FinancialSupportSettings, nullable: true, version: 117 }),
    field({
        decoder: BooleanDecoder,
        version: 320,
        upgrade: (old) => {
            return !!old;
        },
    })
], OrganizationRecordsConfiguration.prototype, "financialSupport", void 0);
__decorate([
    field({ decoder: DataPermissionsSettings, nullable: true, version: 117 }),
    field({
        decoder: BooleanDecoder,
        version: 320,
        upgrade: (old) => {
            return !!old;
        },
    })
], OrganizationRecordsConfiguration.prototype, "dataPermission", void 0);
__decorate([
    field({ decoder: PropertyFilter, nullable: true, version: 124 })
], OrganizationRecordsConfiguration.prototype, "emailAddress", void 0);
__decorate([
    field({ decoder: PropertyFilter, nullable: true, version: 125 })
], OrganizationRecordsConfiguration.prototype, "phone", void 0);
__decorate([
    field({ decoder: PropertyFilter, nullable: true, version: 125 })
], OrganizationRecordsConfiguration.prototype, "gender", void 0);
__decorate([
    field({ decoder: PropertyFilter, nullable: true, version: 125 })
], OrganizationRecordsConfiguration.prototype, "birthDay", void 0);
__decorate([
    field({ decoder: PropertyFilter, nullable: true, version: 125 })
], OrganizationRecordsConfiguration.prototype, "address", void 0);
__decorate([
    field({ decoder: PropertyFilter, nullable: true, version: 125 })
], OrganizationRecordsConfiguration.prototype, "parents", void 0);
__decorate([
    field({ decoder: PropertyFilter, nullable: true, version: 125 })
], OrganizationRecordsConfiguration.prototype, "emergencyContacts", void 0);
__decorate([
    field({ decoder: PropertyFilter, nullable: true, version: 306 })
], OrganizationRecordsConfiguration.prototype, "uitpasNumber", void 0);
__decorate([
    field({ decoder: PropertyFilter, nullable: true, version: 348 })
], OrganizationRecordsConfiguration.prototype, "nationalRegisterNumber", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 420 })
], OrganizationRecordsConfiguration.prototype, "taxCertificates", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(RecordCategory), version: 117 })
], OrganizationRecordsConfiguration.prototype, "recordCategories", void 0);
__decorate([
    field({ decoder: new MapDecoder(StringDecoder, PropertyFilter), version: 254 })
], OrganizationRecordsConfiguration.prototype, "inheritedRecordCategories", void 0);
__decorate([
    field({ decoder: FreeContributionSettings, nullable: true, version: 92 })
], OrganizationRecordsConfiguration.prototype, "freeContribution", void 0);
//# sourceMappingURL=OrganizationRecordsConfiguration.js.map